import type { components } from 'admination-react/api-schema'
import { ApiError, fillPath, getBlob, getJson, sendDelete, sendForm, sendJson } from '@/core/api/http'
import { detailNamesToken, pngBase64 } from './portal'

export type AnwesenheitKarte = components['schemas']['AnwesenheitKarteRead']
type CodeRead = components['schemas']['CodeRead']

const CODE_PATH = '/vergabe-portal/anwesenheit/coaching/{coaching_id}/code'

export async function getAnwesenheitscode(coachingId: string): Promise<string | null> {
  const body = await getJson<CodeRead>(fillPath(CODE_PATH, { coaching_id: coachingId }))
  return body.code ?? null
}

export async function neuerAnwesenheitscode(coachingId: string): Promise<string> {
  const body = await sendJson<CodeRead>(fillPath(CODE_PATH, { coaching_id: coachingId }), 'POST', {})
  if (!body.code) throw new Error("anwesenheitscode: unexpected answer")
  return body.code
}

export function codePruefen(code: string): Promise<AnwesenheitKarte> {
  const body: components['schemas']['CodeIn'] = { code }
  return sendJson<AnwesenheitKarte>('/vergabe-portal/anwesenheit/code/pruefen', 'POST', body)
}

export function anwesenheitUnterschreiben(code: string, pngDataUrl: string): Promise<AnwesenheitKarte> {
  const body: components['schemas']['UnterschriftIn'] = { code, signatur_png_base64: pngBase64(pngDataUrl) }
  return sendJson<AnwesenheitKarte>('/vergabe-portal/anwesenheit/unterschrift', 'POST', body)
}

export const CODE_UNBEKANNT = 'code_unbekannt'
export const AUSSERHALB_ZEITRAUM = 'ausserhalb_zeitraum'
export const BEREITS_UNTERSCHRIEBEN = 'bereits_unterschrieben'
export const KEIN_UNTERRICHTSTAG = "Heute ist kein Unterrichtstag."

export function isCodeUnbekannt(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404 && detailNamesToken(error.detailText, CODE_UNBEKANNT)
}

export function refusalToken(error: unknown): string | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null
  return error.detailText.split(':', 1)[0] ?? null
}

export type AnwesenheitStatus = 'anwesend' | 'nachgeholt' | 'fehlt' | 'offen' | 'kein_unterricht' | 'au' | 'anruf' | 'nachholung'
export type NachholGrund = 'krank' | 'unentschuldigt' | 'sonstiges' | 'vergessen'
export type AnwesenheitTag = {
  status: AnwesenheitStatus
  zeit: string | null
  grund?: NachholGrund
  erlaeuterung?: string | null
  eintrag_id?: string
  eintrag_von?: string
  eintrag_bis?: string
  eingetragen_von?: string | null
  eingetragen_am?: string
}
export type AnwesenheitPerson = {
  coaching_id: string
  name: string
  massnahme_id: string | null
  beginn: string
  ende: string
  zurueckgezogen_am: string | null
  eingeschraenkt_at: string | null
  unentschuldigt: number
  tage: Record<string, AnwesenheitTag>
}
export type Anwesenheitsliste = { personen: AnwesenheitPerson[] }

export function getAnwesenheitsliste(von: string, bis: string): Promise<Anwesenheitsliste> {
  return getJson<Anwesenheitsliste>('/vergabe-portal/anwesenheit/liste', { von, bis })
}

const UNTERRICHTSFREI_PATH = '/vergabe-portal/anwesenheit/massnahme/{massnahme_id}/unterrichtsfrei'
const unterrichtsfreiPath = (massnahmeId: string) => fillPath(UNTERRICHTSFREI_PATH, { massnahme_id: massnahmeId })

export async function getUnterrichtsfrei(massnahmeId: string): Promise<string[]> {
  const body = await getJson<{ tage: string[] }>(unterrichtsfreiPath(massnahmeId))
  return body.tage
}

export function unterrichtsfreiHinzufuegen(massnahmeId: string, datum: string): Promise<void> {
  return sendJson<void>(`${unterrichtsfreiPath(massnahmeId)}/${datum}`, 'PUT', {})
}

export function unterrichtsfreiEntfernen(massnahmeId: string, datum: string): Promise<void> {
  return sendDelete(`${unterrichtsfreiPath(massnahmeId)}/${datum}`)
}

type EintragRead = components['schemas']['EintragRead']
const AU_PATH = '/vergabe-portal/anwesenheit/au/{au_id}'
const ANRUF_PATH = '/vergabe-portal/anwesenheit/anruf/{anruf_id}'

export const AU_MAX_BYTES = 5 * 1024 * 1024

export function auAnlegen(coachingId: string, beginn: string, ende: string, datei: File): Promise<EintragRead> {
  const form = new FormData()
  form.append('coaching_id', coachingId)
  form.append('beginn', beginn)
  form.append('ende', ende)
  form.append('datei', datei, datei.name)
  return sendForm<EintragRead>('/vergabe-portal/anwesenheit/au', form)
}

export const auDatei = (id: string): Promise<Blob> => getBlob(fillPath('/vergabe-portal/anwesenheit/au/{au_id}/datei', { au_id: id }))
export const auEntfernen = (id: string): Promise<void> => sendDelete(fillPath(AU_PATH, { au_id: id }))

export function anrufAnlegen(coachingId: string, angerufenAm: string, bis: string): Promise<EintragRead> {
  const body: components['schemas']['AnrufIn'] = { coaching_id: coachingId, angerufen_am: angerufenAm, bis }
  return sendJson<EintragRead>('/vergabe-portal/anwesenheit/anruf', 'POST', body)
}

export const anrufEntfernen = (id: string): Promise<void> => sendDelete(fillPath(ANRUF_PATH, { anruf_id: id }))

export async function nachholenTage(coachingId: string): Promise<string[]> {
  const { tage } = await getJson<components['schemas']['NachholenListeRead']>('/vergabe-portal/anwesenheit/nachholen', { coaching_id: coachingId })
  return tage
}

export const nachholenPruefen = (coachingId: string, datum: string): Promise<void> =>
  sendJson<void>('/vergabe-portal/anwesenheit/nachholen/pruefen', 'POST', { coaching_id: coachingId, datum })

export function nachholenUnterschreiben(coachingId: string, datum: string, grund: NachholGrund, erlaeuterung: string, pngDataUrl: string): Promise<components['schemas']['NachholungRead']> {
  const body: components['schemas']['NachholenIn'] = { coaching_id: coachingId, datum, grund, erlaeuterung: erlaeuterung.trim() || null, signatur_png_base64: pngBase64(pngDataUrl) }
  return sendJson<components['schemas']['NachholungRead']>('/vergabe-portal/anwesenheit/nachholen', 'POST', body)
}
